import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { initializePaddle } from "@paddle/paddle-js";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import { Check, Sparkles, Loader2, ShieldCheck, Zap } from "lucide-react";

export default function Billing() {
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [paddle, setPaddle] = useState(null);
  const [interval, setInterval] = useState(params.get("interval") === "month" ? "month" : "year");
  const isPaid = user?.subscription_status === "active";

  useEffect(() => {
    (async () => {
      const u = await refreshUser();
      if (!u?.is_paid) {
        try { await api.post("/payments/reconcile"); await refreshUser(); } catch {}
      }
    })();
  }, []);

  useEffect(() => {
    initializePaddle({
      environment: "production",
      token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN,
      eventCallback: (event) => {
        if (event?.name === "checkout.completed") pollForPro();
      },
    }).then((p) => setPaddle(p)).catch(() => {});
  }, []);

  const pollForPro = () => {
    setLoading(true);
    let attempts = 0;
    const timer = window.setInterval(async () => {
      attempts += 1;
      try { await api.post("/payments/reconcile"); } catch {}
      const u = await refreshUser();
      if (u?.is_paid) {
        window.clearInterval(timer);
        setLoading(false);
        toast.success("You're now Pro — unlimited searches & downloads unlocked.");
      } else if (attempts > 15) {
        window.clearInterval(timer);
        setLoading(false);
        toast.message("Payment received. Pro will activate in a moment — refresh if needed.");
      }
    }, 2000);
  };

  const upgrade = () => {
    if (!paddle) { toast.error("Checkout is still loading — try again in a second."); return; }
    const priceId = interval === "year"
      ? process.env.REACT_APP_PADDLE_PRICE_ANNUAL
      : process.env.REACT_APP_PADDLE_PRICE_MONTHLY;
    paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customer: user?.email ? { email: user.email } : undefined,
      customData: { user_id: String(user?.user_id || "") },
      settings: { displayMode: "overlay", theme: "dark", locale: "en", allowLogout: false },
    });
  };

  const activeInterval = isPaid ? (user?.plan_interval || "month") : interval;
  const price = activeInterval === "year" ? "$99" : "$12";
  const per = activeInterval === "year" ? "/year" : "/month";

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto animate-fade-up">
        <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Subscription</h1>
        <p className="text-slate-400 mb-6">You're on the <span className="text-slate-200 font-medium">{isPaid ? "Pro" : "Free"}</span> plan.</p>

        <div className="rounded-xl border border-blue-500/25 bg-blue-600/[0.06] px-4 py-3 mb-8 text-sm text-slate-300">
          <span className="text-blue-300 font-medium">How it works:</span> free accounts can upload one dataset, build the dashboard, and run <span className="text-white font-medium">3 searches</span> (view-only). Go Pro for unlimited searches, unlimited datasets, and PDF + Excel downloads.
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div className={`rounded-2xl border p-7 ${!isPaid ? "border-blue-500/40 bg-blue-600/[0.05]" : "border-[#27334D] bg-[#111726]"}`}>
            <div className="label-mono text-slate-500 mb-2">Free</div>
            <div className="font-display text-4xl font-bold mb-4">$0</div>
            <ul className="space-y-2.5 text-sm text-slate-400 mb-6">
              {["1 dataset upload", "Auto-built dashboard & filters", "3 searches (view-only)", "No PDF / Excel downloads"].map((t) => <li key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-slate-500" /> {t}</li>)}
            </ul>
            {!isPaid && <div className="text-center text-xs font-mono-niq text-blue-300 py-2">Current plan</div>}
          </div>

          <div className={`rounded-2xl border p-7 relative ${isPaid ? "border-emerald-500/40 bg-emerald-600/[0.05]" : "border-blue-500/40 bg-blue-600/[0.06]"}`}>
            <div className="label-mono text-blue-300 mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Pro</div>

            {!isPaid && (
              <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-[#0D121C] border border-[#27334D] mb-4" data-testid="billing-interval-toggle">
                <button onClick={() => setInterval("month")} data-testid="interval-month" className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${interval === "month" ? "bg-blue-600 text-white" : "text-slate-400"}`}>Monthly</button>
                <button onClick={() => setInterval("year")} data-testid="interval-year" className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${interval === "year" ? "bg-blue-600 text-white" : "text-slate-400"}`}>Yearly <span className="text-[9px] text-emerald-400 font-mono-niq">−31%</span></button>
              </div>
            )}

            <div className="font-display text-4xl font-bold mb-1" data-testid="pro-price">{price}<span className="text-lg text-slate-500 font-normal">{per}</span></div>
            <div className="text-xs text-slate-500 mb-4">{activeInterval === "year" ? "Billed yearly · ~$8.25/month" : "Billed monthly"}</div>
            <ul className="space-y-2.5 text-sm text-slate-300 mb-6">
              {["Unlimited searches", "Unlimited PDF + Excel downloads", "Unlimited datasets", "Priority AI query reasoning", "Cancel auto-renewal anytime"].map((t) => <li key={t} className="flex items-center gap-2"><Zap className="w-4 h-4 text-blue-400" /> {t}</li>)}
            </ul>
            {isPaid ? (
              <div className="text-center text-sm font-medium text-emerald-400 py-2 flex items-center justify-center gap-2"><ShieldCheck className="w-4 h-4" /> Active</div>
            ) : (
              <button onClick={upgrade} disabled={loading} data-testid="upgrade-button" className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-3 rounded-lg font-medium text-sm transition-colors disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Subscribe · ${price}${per}`}
              </button>
            )}
          </div>
        </div>
        {!isPaid && <p className="text-center text-xs text-slate-600 mt-6 font-mono-niq">Secure checkout by Paddle · cancel anytime · VAT handled at checkout</p>}
      </div>
    </AppShell>
  );
}
